import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TextInput, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { Picker } from "react-native";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { AntDesign } from "@expo/vector-icons";
import { DatePickerInput } from "react-native-paper-dates";
import {
  approveIncomingOrder,
  approveSingleIncomingOrder,
  updateIncomingOrder,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
  selectedEnquiryType: string;
}

const fullHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.3,
    fontWeight: 700,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.3,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.3,
    fontWeight: 700,
  },
  // AusInventory: {
  //   name: "Aus Inventory",
  //   width: 0.3,
  //   fontWeight: 700,
  // },
  ShippingDeadline: {
    name: "Shipping Deadline",
    width: 0.3,
    fontWeight: 700,
  },
  incomingStatus: {
    mame: "Incoming Status",
    width: 0.4,
    textAlign: "left",
  },
};

const mediumHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.4,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.3,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority",
    width: 0.3,
    fontWeight: 700,
  },
  ArtworkReference: {
    name: "Art Ref",
    width: 0.3,
    fontWeight: 700,
  },
  ETA: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  incomingStatus: {
    mame: "Incoming Status",
    width: 0.4,
    textAlign: "left",
  },
};

const smallHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.4,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.3,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority",
    width: 0.3,
    fontWeight: 700,
  },
  incomingStatus: {
    mame: "Incoming Status",
    width: 0.4,
    textAlign: "left",
  },
};

const ChinaTrackerRejectModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
  selectedEnquiryType,
}: any) => {
  const { t } = useTranslation();
  const listRef = useRef<ScrollView>(null);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height
  );
  const translateIncomingStatus = (status: string) => {
    switch (status) {
      case "INCOMING":
        return t("incoming");
      case "APPROVED":
        return t("approved");
      case "REJECTED":
        return t("rejected");
      default:
        return t("revised_by_purchasing");
    }
  };

  const translatePriority = (priority: string) => {
    switch (priority) {
      case "High":
        return t("high");
      case "Medium":
        return t("medium");
      default:
        return t("normal");
    }
  };

  // Initialize state with order translated properties
  const [selectedOrder, setSelectedOrder] = useState({
    ...order,
    incomingStatus: translateIncomingStatus(order.incomingStatus),
    PriorityOrder: translatePriority(order.Priority),
  });

  const { language } = useSelector((state: any) => state.configurations);

  const [isSearched, setIsSearched] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const loading = false;

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [tableHeaders, setTableHeaders] = useState<any>(fullHeaders);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const [, setContentOffSet] = useState<number>(0);

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  useEffect(() => {
    let isMounted: boolean = true;

    setTableHeaders(
      isLargeDevice
        ? fullHeaders
        : isMediumDevice
        ? mediumHeaders
        : smallHeaders
    );

    setTableHeaders(
      isLargeDevice
        ? translateHeaders(fullHeaders, t, chinaTrackerTranslationMap)
        : isMediumDevice
        ? translateHeaders(mediumHeaders, t, chinaTrackerTranslationMap)
        : translateHeaders(smallHeaders, t, chinaTrackerTranslationMap)
    );
    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [
    isRefresh,
    isLargeDevice,
    isMediumDevice,
    isSmallDevice,
    screenWidth,
    language,
  ]);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "90%";
    if (isMediumDevice) return "80%";
    return "76%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };
  // Hook form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const subject = watch("subject");

  const onSubmit = (data: any) => {
    // Base payload structure
    const payload: any = {
      orderNumber: order.OrderNumber1,
      subject: data.subject,
      message: data.message,
      status: "REJECTED",
      key: order.Key,
    };

    // Add conditional fields based on subject selection
    if (data.subject === "NEW_DUE_DATE" && data.newDueDate) {
      payload.dueDate = data.newDueDate;
    }

    if (data.subject === "NEW_ORDER_QTY" && data.newOrderQty) {
      payload.orderQty = parseInt(data.newOrderQty);
    }

    if (data.subject === "NEW_SHIPPING_DEADLINE" && data.newShippingDeadline) {
      payload.shippingDeadline = data.newShippingDeadline;
    }

    if (selectedEnquiryType === "update_reject") {
      dispatch(updateIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Order rejected successfully", {
            type: "success",
          });
          onClose();
        })
        .catch((error: any) => {
          toast.show("Error occurred during approval of incoming order", {
            type: "danger",
          });
        });
    } else {
      dispatch(approveSingleIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Order rejected successfully", {
            type: "success",
          });
          onClose();
        })
        .catch((error: any) => {
          toast.show("Error occurred during approval of incoming order", {
            type: "danger",
          });
        });
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          <View
            style={[
              styles.modalContainerSecond,
              { width: getInnerContainerWidth() },
            ]}
          >
            <View style={styles.titleContainer}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <TouchableOpacity onPress={onClose}>
                  <AntDesign name="arrowleft" size={37} color="black" />
                </TouchableOpacity>

                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {translateFields(
                    "RejectOrderNumber",
                    t,
                    chinaTrackerTranslationMap
                  )}{" "}
                  - {order.OrderNumber1}
                </Text>
              </View>
            </View>

            <ScrollView onScroll={handleScroll} ref={listRef}>
              <View>
                <View>
                  <View style={styles.containerContent}>
                    {!loading ? (
                      <View>
                        <DataTableWithPagination
                          headers={tableHeaders}
                          rows={selectedOrder ? [selectedOrder] : []}
                          itemsPerPage={10}
                          isDataLoading={isSearched}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          setModalVisible={() => {}}
                          handleRowClick={() => {}}
                          setSelected={() => {}}
                          handleDeleteRow={() => {}}
                          itemTracker={true}
                        />
                      </View>
                    ) : (
                      <View>
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      </View>
                    )}
                  </View>

                  {/* Form fields section */}
                  <View style={styles.formSection}>
                    <View style={styles.formField}>
                      <Text
                        style={[
                          styles.inputLabel,
                          isSmallDevice && styles.inputLabelSmall,
                        ]}
                      >
                        {translateFields(
                          "Subject",
                          t,
                          chinaTrackerTranslationMap
                        )}
                      </Text>

                      <View style={[styles.picker]}>
                        <Controller
                          control={control}
                          name="subject"
                          rules={{ required: "Subject is required" }}
                          render={({ field: { onChange, value } }) => (
                            <Picker
                              selectedValue={value}
                              onValueChange={onChange}
                              style={[
                                styles.pickerInput,
                                isSmallDevice && styles.inputSmall,
                                {
                                  borderWidth: 0,
                                  backgroundColor: "transparent",
                                },
                              ]}
                              dropdownIconColor="#000"
                              mode="dropdown"
                              underlineColorAndroid="transparent"
                            >
                              <Picker.Item
                                label={t("selectasubejct")}
                                value=""
                              />
                              <Picker.Item
                                label={t("newduedate")}
                                value="NEW_DUE_DATE"
                              />
                              <Picker.Item
                                label={t("neworderqty")}
                                value="NEW_ORDER_QTY"
                              />
                              <Picker.Item
                                label={t("newshippingdeadline")}
                                value="NEW_SHIPPING_DEADLINE"
                              />
                            </Picker>
                          )}
                        />
                      </View>
                      {errors.subject && (
                        <Text style={styles.errorText}>
                          {errors.subject.message as string}
                        </Text>
                      )}
                    </View>

                    {subject && subject === "NEW_DUE_DATE" && (
                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {translateFields(
                            "NewDueDate",
                            t,
                            chinaTrackerTranslationMap
                          )}
                        </Text>
                        <Controller
                          control={control}
                          name="newDueDate"
                          rules={{
                            required: "Due Date is required",
                            pattern: {
                              value: /^\d{2}\/\d{2}\/\d{4}$/, // Validates the format DD/MM/YYYY
                              message: "Please enter a valid date (DD/MM/YYYY)",
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <DatePickerInput
                                testID="dateTimePicker"
                                value={value || new Date()}
                                mode="date"
                                onChange={onChange}
                                style={{ backgroundColor: "white" }}
                              />
                            </>
                          )}
                        />
                        {errors.newDueDate && (
                          <Text style={styles.errorText}>
                            {errors.newDueDate.message as string}
                          </Text>
                        )}
                      </View>
                    )}

                    {subject && subject === "NEW_ORDER_QTY" && (
                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {translateFields(
                            "NewOrderQty",
                            t,
                            chinaTrackerTranslationMap
                          )}
                        </Text>

                        <Controller
                          control={control}
                          name="newOrderQty"
                          rules={{
                            required: "Order Qty is required",
                            pattern: {
                              value: /^[0-9]+$/, // Only numbers
                              message: "Please enter a valid quantity",
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                backgroundColor: "white",
                                borderWidth: 1,
                                borderColor: "#ccc",
                                borderRadius: 6,
                                overflow: "hidden",
                              }}
                            >
                              <TextInput
                                value={value}
                                onChangeText={onChange}
                                keyboardType="numeric"
                                placeholder={translateFields(
                                  "EnterQuantity",
                                  t,
                                  chinaTrackerTranslationMap
                                )}
                                style={{
                                  flex: 1,
                                  padding: 10,
                                  fontSize: 16,
                                }}
                              />
                              <TouchableOpacity
                                style={{
                                  backgroundColor: "#f0f0f0",
                                  paddingHorizontal: 12,
                                  paddingVertical: 10,
                                  borderLeftWidth: 1,
                                  borderLeftColor: "#ccc",
                                }}
                                disabled
                              >
                                <Text style={{ fontSize: 16, color: "#333" }}>
                                  Boxes
                                </Text>
                              </TouchableOpacity>
                            </View>
                          )}
                        />

                        {errors.newOrderQty && (
                          <Text style={styles.errorText}>
                            {errors.newOrderQty.message as string}
                          </Text>
                        )}
                      </View>
                    )}

                    {subject && subject === "NEW_SHIPPING_DEADLINE" && (
                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {translateFields(
                            "NewShippingDeadline",
                            t,
                            chinaTrackerTranslationMap
                          )}
                        </Text>
                        <Controller
                          control={control}
                          name="newShippingDeadline"
                          rules={{
                            required: "Shipping Deadline is required",
                            pattern: {
                              value: /^\d{2}\/\d{2}\/\d{4}$/, // Validates the format DD/MM/YYYY
                              message: "Please enter a valid date (DD/MM/YYYY)",
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <DatePickerInput
                                testID="dateTimePicker"
                                value={value || new Date()}
                                mode="date"
                                onChange={onChange}
                                style={{ backgroundColor: "white" }}
                              />
                            </>
                          )}
                        />
                        {errors.newShippingDeadline && (
                          <Text style={styles.errorText}>
                            {errors.newShippingDeadline.message as string}
                          </Text>
                        )}
                      </View>
                    )}

                    <View style={styles.formField}>
                      <Text
                        style={[
                          styles.inputLabel,
                          isSmallDevice && styles.inputLabelSmall,
                        ]}
                      >
                        {translateFields(
                          "message",
                          t,
                          chinaTrackerTranslationMap
                        )}
                      </Text>
                      <Controller
                        control={control}
                        name="message"
                        rules={{ required: "Message is required" }}
                        render={({ field: { onChange, value } }) => (
                          <TextInput
                            value={value}
                            onChangeText={onChange}
                            style={[
                              styles.messageInput,
                              isSmallDevice && styles.messageInputSmall,
                            ]}
                            multiline
                            numberOfLines={isSmallDevice ? 3 : 4}
                          />
                        )}
                      />
                      {errors.message && (
                        <Text style={styles.errorText}>
                          {errors.message.message as string}
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            </ScrollView>
            <View style={[styles.footer, isSmallDevice && styles.footerSmall]}>
              <TouchableOpacity
                onPress={onClose}
                style={[
                  styles.closeButton,
                  isSmallDevice && styles.buttonSmall,
                ]}
              >
                <Text style={isSmallDevice && styles.buttonTextSmall}>
                  {t("close")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSubmit(onSubmit)}
                style={[
                  styles.submitButton,
                  isSmallDevice && styles.buttonSmall,
                ]}
              >
                <Text
                  style={[
                    styles.submitText,
                    isSmallDevice && styles.submitTextSmall,
                  ]}
                >
                  {t("submit")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  modalContainer: {
    backgroundColor: "#fafafa",
    padding: 25,
    elevation: 8,
    maxHeight: "90%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 8,
  },
  titleContainer: {
    borderBottomWidth: 1,
    borderBottomColor: "#A9A9A9",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    paddingVertical: 10,
    marginBottom: 20,
    shadowColor: "#000000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  titleText: {
    fontSize: 25,
    fontWeight: "bold",
    marginLeft: 15,
  },
  titleTextSmall: {
    fontSize: 16,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 35,
  },
  formField: {
    marginBottom: 15,
  },
  input: {
    padding: 10,
    height: 40,
  },
  pickerInput: {
    padding: 10,
    height: 45,
  },
  inputSmall: {
    padding: 5,
    height: 35,
    fontSize: 12,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
  },
  inputLabelSmall: {
    fontSize: 12,
    marginBottom: 3,
  },
  picker: {
    borderWidth: 1,
    borderColor: "#ccc",
    height: 40,
    backgroundColor: "#fff",
    borderRadius: 5,
    paddingHorizontal: 10,
    justifyContent: "center",
  },
  pickerSmall: {
    height: 35,
    paddingHorizontal: 5,
    fontSize: 12,
  },
  messageInput: {
    height: 100,
    borderWidth: 1,
    borderColor: "#ccc",
    padding: 10,
    backgroundColor: "#fff",
    marginBottom: 15,
    borderRadius: 5,
    textAlignVertical: "top",
  },
  messageInputSmall: {
    height: 80,
    padding: 5,
    fontSize: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
  },
  submitButton: {
    backgroundColor: "#000",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  submitText: {
    color: "#fff",
    fontWeight: "bold",
  },
  submitTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 25,
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});

export default ChinaTrackerRejectModal;
